import * as React from 'react';

import classNames from 'classnames';

import {safelyGetGraphQLErrorMessage} from 'nutshell-core/utils/graphql-errors';

import {SaveButton} from '../../button/save-button/save-button';
import {colors} from '../../colors/colors';
import type {ColorName} from '../../colors/colors';
// @ts-expect-error - imported from .js/.jsx file
import {NutshellLogoMarkIcon} from '../../icon/svg/nutshell-logo-mark-icon';
import {Modal} from '../../modal/modal';
import {TextField} from '../../textfield/textfield';
import {Body} from '../../typography/body';

import styles from './internal-survey-modal.module.css';

type Props = {
    thumbnailIllustration?: string;
    thumbnailBackgroundColor?: ColorName;
    title: string;
    description: string;
    onSubmit: (response: string) => Promise<any>;
    onClose: () => void;
};

export function InternalSurveyModal(props: Props) {
    const {thumbnailBackgroundColor = 'blueXlt', onSubmit} = props;

    const [textFieldValue, setTextFieldValue] = React.useState<string>('');
    const [mode, setMode] = React.useState<'form' | 'thanks'>('form');
    const [isSaving, setIsSaving] = React.useState<boolean>(false);
    const [errorMessage, setErrorMessage] = React.useState<string | null | undefined>(null);

    const handleSubmit = React.useCallback(() => {
        setErrorMessage(null);
        setIsSaving(true);
        onSubmit(textFieldValue)
            .then(() => {
                setIsSaving(false);
            })
            .catch((error) => {
                setErrorMessage(safelyGetGraphQLErrorMessage(error));
                setIsSaving(false);
            });
    }, [onSubmit, textFieldValue]);

    const handleKeyDown = React.useCallback(
        (e: React.KeyboardEvent<any>): void => {
            if (!textFieldValue) return;

            if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
                e.preventDefault();
                handleSubmit();
            }
        },
        [handleSubmit, textFieldValue]
    );

    const contentStyleNames = classNames(styles['content-container'], {
        [styles['content-container--align-center']]: mode === 'thanks',
        [styles['content-container--no-illustration']]: !props.thumbnailIllustration,
    });

    const titleStyleNames = classNames({
        [styles['modal-title--no-illustration']]: !props.thumbnailIllustration,
    });

    return (
        <Modal
            onClose={props.onClose}
            isOpen={true}
            closeButton={true}
            maxWidth={400}
            fitHeight={true}
            noPadding={true}
            isCentered={true}
        >
            {props.thumbnailIllustration && (
                <div
                    style={{
                        backgroundColor:
                            thumbnailBackgroundColor && colors[thumbnailBackgroundColor],
                    }}
                    className={styles['thumbnail-container']}
                >
                    <img src={props.thumbnailIllustration} alt='Info graphic' />
                </div>
            )}
            <div className={contentStyleNames}>
                {mode === 'form' ? (
                    <>
                        <div className={titleStyleNames}>
                            <Body isBold={true} color='dark' maintainLineBreaks={true}>
                                {props.title}
                            </Body>
                        </div>
                        <Body size='small' maintainLineBreaks={true}>
                            {props.description}
                        </Body>
                        <TextField
                            value={textFieldValue}
                            onChange={setTextFieldValue}
                            focusOnMount={true}
                            multiline={true}
                            rows={5}
                            placeholder='Let us know what you think…'
                            onKeyDown={handleKeyDown}
                        />
                        {errorMessage && (
                            <Body size='small' color='error'>
                                {errorMessage}
                            </Body>
                        )}
                        <SaveButton
                            // @ts-expect-error
                            onClick={handleSubmit}
                            errorSaving={Boolean(errorMessage)}
                            onClearSaveComplete={() => setMode('thanks')}
                            saveCompleteTimeout={1500}
                            variant='primary'
                            disabled={!textFieldValue}
                            buttonText='Submit feedback'
                            savingText='Submitting feedback…'
                            successText='Feedback submitted!'
                            isSaving={isSaving}
                            disabledTooltip='Add feedback before submitting!'
                        />
                    </>
                ) : (
                    <>
                        <Body isBold={true} size='normal' color='dark'>
                            Thanks for your feedback!
                        </Body>
                        <NutshellLogoMarkIcon size={32} fill={colors.orange} />
                        <Body size='small' isItalic={true}>
                            Happy Nutshelling
                        </Body>
                    </>
                )}
            </div>
        </Modal>
    );
}
